import React from 'react';
import { Flex, Select } from '@radix-ui/themes';
import { observer } from 'mobx-react-lite';

import { AutocompleteV2 } from 'shared/components/autocomplete-v2';
import { FormField } from 'shared/components/form-field';
import { SearchIcon } from 'shared/icons';

import {
  CountryCityCountry,
  CountryCitySelectorStore,
  formatCountryCityLabel,
} from './country-city-selector.store';

export type CountryCitySelectorProps = {
  store: CountryCitySelectorStore;
  countries: CountryCityCountry[];
  countryLabel?: string;
  cityLabel?: string;
  countryPlaceholder?: string;
  cityPlaceholder?: string;
  disabledCityPlaceholder?: string;
  requiredCountry?: boolean;
  requiredCity?: boolean;
  color?: React.ComponentProps<typeof Select.Trigger>['color'];
};

const CountryCitySelectorView = ({
  store,
  countries,
  countryLabel = 'Країна',
  cityLabel = 'Місто',
  countryPlaceholder = 'Виберіть країну',
  cityPlaceholder = 'Введіть назву міста',
  disabledCityPlaceholder = 'Спочатку оберіть країну',
  requiredCountry = false,
  requiredCity = false,
  color = 'indigo',
}: CountryCitySelectorProps) => {
  return (
    <Flex gap="4">
      <FormField label={countryLabel} required={requiredCountry}>
        <Select.Root
          size="3"
          value={store.countryId}
          onValueChange={store.setCountryId}
        >
          <Select.Trigger color={color} placeholder={countryPlaceholder} />
          <Select.Content color={color} position="popper">
            {countries.map((country) => (
              <Select.Item key={country.id} value={String(country.id)}>
                {country.name}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
      </FormField>

      <FormField label={cityLabel} required={requiredCity}>
        <AutocompleteV2.Root
          items={store.cityAutocompleteOptions}
          viewModel={store.cityVM}
          getOptionValue={(city) => city.id}
          getOptionLabel={formatCountryCityLabel}
          color={color}
          disabled={!store.countryId}
          indicator={<SearchIcon width={20} height={20} />}
          rotateIndicatorOnOpen={false}
          loading={store.isCityLoading}
          placeholder={store.countryId ? cityPlaceholder : disabledCityPlaceholder}
          resetInputOnClose="never"
          onSearch={store.onSearchCity}
          onSelect={store.onSelectCity}
        />
      </FormField>
    </Flex>
  );
};

export const CountryCitySelector = observer(CountryCitySelectorView);
